import {
  Box,
  Container,
  Text,
  Image,
  Flex,
  Heading,
  FlexProps,
  SimpleGrid,
  Grid,
  UnorderedList,
  ListItem,
  VStack,
  Stack,
} from '@chakra-ui/react';
import { Header } from '../../atoms/Header';
import { RevealOnScroll } from '../../atoms/RevealOnScroll';
import Blocks2 from '../../../assets/the-problem.png';
import { Card } from '../../molecules/Card';
import { Icon } from 'src/components/atoms/Icon';
import { SOLUTION_IMAGES } from 'src/features/MP/utils/constants';
import { imageZoomSx } from 'src/features/MP/utils/imageZoomSx';

export const MainBlocks: React.FC<FlexProps> = () => {
  return (
    <Box bg="white" color="text.darkBlue.900" pt={{ base: 16, md: 24 }}>
      <RevealOnScroll>
        <Container maxW={'container.xl'}>
          <Header>
            Why Merkatus.io?
            <Text
              fontSize="xl"
              color="gray.600"
              fontFamily="marketingPage"
              fontWeight="medium"
              textTransform="uppercase"
              mb={{ base: 8, md: 12 }}>
              Combining automation, marketing planning expertise, and AI to help
              businesses make <br /> faster, smarter marketing decisions.
            </Text>
          </Header>

          <Box
            as="div"
            borderRadius="2xl"
            py={6}
            px={9}
            textAlign="left"
            bg="background.lightGrey"
            mb={6}>
            <Flex align="center">
              <Heading
                as="h3"
                size="md"
                fontFamily="marketingPage"
                fontWeight="medium"
                textTransform={'uppercase'}>
                Everything you need to turn marketing complexity into clarity,
                built for companies investing €100,000+ annually in media.
              </Heading>
            </Flex>
          </Box>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            <Card
              iconName="gear"
              color="text.darkBlue.900"
              headerText="AUTOMATION & EFFICIENCY">
              <Text
                lineHeight="24px"
                fontSize="sm"
                color="gray.600"
                fontFamily="marketingPage">
                Cut the complexity. Focus on impact.{' '}
              </Text>
              <Text
                mt={2}
                lineHeight="24px"
                fontSize="sm"
                color="gray.600"
                fontFamily="marketingPage">
                Marketing Mix Modeling no longer needs to be slow or expensive.
                With Merkatus.io, you get:
              </Text>

              <UnorderedList
                spacing={2}
                mt={4}
                color="gray.600"
                fontSize="sm"
                fontFamily="marketingPage">
                <ListItem>AI-powered modeling in hours and not days</ListItem>
                <ListItem>Automated data integrations</ListItem>
                <ListItem>Always-on measurement tools</ListItem>
              </UnorderedList>

              {/* Compass line */}
              <Text
                mt={4}
                fontSize="sm"
                color="gray.600"
                fontFamily="marketingPage">
                🚀 Make confident decisions with less effort
              </Text>
            </Card>

            <Card iconName="location" headerText="INDEPENDENT & PURPOSE-BUILT">
              {/* Two-line intro */}
              <Text
                lineHeight="24px"
                fontSize="sm"
                color="gray.600"
                fontFamily="marketingPage">
                Made by marketers. Designed for action.
              </Text>
              <Text
                mt={2}
                lineHeight="24px"
                fontSize="sm"
                color="gray.600"
                fontFamily="marketingPage">
                Most modeling and AI tools are built for analysts, ad platforms
                or media agencies. Merkatus.io is different:
              </Text>

              {/* Bullet list */}
              <UnorderedList
                spacing={2}
                mt={4}
                color="gray.600"
                fontSize="sm"
                fontFamily="marketingPage">
                <ListItem>Built by marketing and media experts</ListItem>
                <ListItem>Platform-neutral and fully independent </ListItem>
                <ListItem>
                  Designed for usability, not complexity and actionable for
                  teams of any size
                </ListItem>
              </UnorderedList>

              {/* Compass line */}
              <Text
                mt={4}
                fontSize="sm"
                color="gray.600"
                fontFamily="marketingPage">
                🎯 Built to support real decisions — not just reports
              </Text>
            </Card>
            <Card iconName="ai" headerText="AI-DRIVEN RECOMMENDATIONS">
              {/* Two-line intro */}
              <Text
                lineHeight="24px"
                fontSize="sm"
                color="gray.600"
                fontFamily="marketingPage">
                Smarter media investments. Measurable growth.
              </Text>
              <Text
                mt={2}
                lineHeight="24px"
                fontSize="sm"
                color="gray.600"
                fontFamily="marketingPage">
                Get actionable insights that tie your marketing directly to
                sales.
              </Text>

              {/* Bullet list */}
              <UnorderedList
                spacing={2}
                mt={4}
                color="gray.600"
                fontSize="sm"
                fontFamily="marketingPage">
                <ListItem>
                  Bayesian modeling across channels & time horizons
                </ListItem>
                <ListItem>
                  Real ROI attribution, not just short-term uplift
                </ListItem>
                <ListItem>
                  Up to +18% increase in sales with MMM optimization
                </ListItem>
              </UnorderedList>

              {/* Compass line */}
              <Text
                mt={4}
                fontSize="sm"
                color="gray.600"
                fontFamily="marketingPage">
                📊 Go from intuition to evidence-based strategy.
              </Text>
            </Card>
          </SimpleGrid>
        </Container>
      </RevealOnScroll>
      <RevealOnScroll>
        <Container maxW="container.xl" mt={16}>
          <RevealOnScroll direction="left">
            <Header mb={6}>THE PROBLEM</Header>
          </RevealOnScroll>

          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
            gap={6}
            w="100%">
            {/* Text box: second on mobile, first on desktop */}
            <RevealOnScroll direction="left">
              <Box
                order={{ base: 2, md: 1 }}
                bg="background.lightGrey"
                lineHeight="24px"
                fontFamily="marketingPage"
                fontSize="sm"
                p={9}
                borderRadius="2xl"
                color="gray.600">
                <Text fontWeight="bold">
                  Marketing is expected to deliver growth and prove it.
                </Text>

                <Text mt={4}>
                  It&apos;s no longer a cost center but a strategic investment.
                  Yet proving return has become harder than ever.
                </Text>

                <Text mt={4}>
                  The media landscape is fragmented. Data is everywhere but
                  rarely aligned. CMOs face mounting pressure to justify spend,
                  often relying on siloed insights from platform giants like
                  Google, Meta, or Amazon. This narrows the view and distorts
                  the bigger picture.
                </Text>

                <Text mt={4}>
                  Marketing Mix Modeling (MMM) remains the only science-backed
                  way to measure true business impact. But most traditional MMM
                  solutions are built for enterprises and often slow, expensive,
                  and out of reach for agile teams.
                </Text>

                <Text mt={4} fontWeight="bold">
                  And most MMM tools?
                </Text>

                <UnorderedList spacing={2} mt={2}>
                  <ListItem>
                    Are black-box solutions, focused only on digital or
                    short-term results
                  </ListItem>
                  <ListItem>Deliver insights too late to act on</ListItem>
                  <ListItem>
                    Miss local media behavior, campaign nuance, and seasonal
                    shifts
                  </ListItem>
                </UnorderedList>

                <Text mt={4} fontWeight="bold">
                  The result?
                </Text>

                <Text mt={2}>
                  Marketers are left guessing what&apos;s working. Budgets drift
                  toward incomplete data. And sometimes, teams make decisions
                  that unintentionally reduce ROI.
                </Text>

                <Text mt={4}>
                  It doesn&apos;t have to be this way. With the right data,
                  smart modeling, and GenAI, you can cut through the noise and
                  finally see what&apos;s driving results.
                </Text>
              </Box>
            </RevealOnScroll>

            {/* Image box: first on mobile, second on desktop */}
            <RevealOnScroll
              direction="right"
              order={{ base: 1, md: 2 }}
              bg="background.lightGrey"
              borderRadius="2xl"
              display="flex"
              alignItems="end"
              p={0}
              overflow="hidden">
              <Image sx={imageZoomSx} src={Blocks2} w="100%" h="100%" />
            </RevealOnScroll>
          </Grid>
        </Container>
      </RevealOnScroll>

      <RevealOnScroll>
        <Box bg="white" color="gray.800" pt={{ base: 16, md: 24 }}>
          <Container maxW="container.xl">
            <Header>
              The Solution: Clarity, speed, and control powered by Merkatus.io
              <Text
                fontSize="xl"
                color="gray.600"
                fontFamily="marketingPage"
                fontWeight="medium"
                mb={{ base: 8, md: 12 }}>
                You don’t need complex custom projects or biased platform
                dashboards to understand what’s driving your results.
                Merkatus.io gives you a smarter, faster way to model marketing
                impact and act with confidence.
              </Text>
            </Header>

            <VStack spacing={12} align="stretch">
              {/* Main title */}
              <Box
                bg="background.lightGrey"
                borderRadius="xl"
                p={6}
                boxShadow="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={4}>
                <Icon
                  iconName="circle-checkbox"
                  width={6}
                  height={6}
                  fill={'white'}
                  color={'icon.green'}
                />
                <Text
                  fontSize={{ base: 'lg', md: 'xl', lg: '21px' }}
                  fontWeight="medium"
                  color="gray.800"
                  textTransform="uppercase">
                  ONE PLATFORM. BUILT FOR HOW MODERN MARKETING ACTUALLY WORKS:
                </Text>
              </Box>

              <VStack gap={6} width="100%">
                {/* Row 1: Unified Marketing ROI Dashboard + Placeholder */}
                <Stack
                  gap={6}
                  width="100%"
                  direction={{ base: 'column', md: 'row' }}>
                  <Box
                    maxWidth={{ base: '100%', md: '543px' }}
                    width="100%"
                    height="336px">
                    <VStack
                      p={6}
                      bg="background.lightGrey"
                      borderRadius="xl"
                      height="336px"
                      justifyContent={'space-between'}
                      align="start">
                      <Icon
                        iconName="display"
                        width={14}
                        height={14}
                        color="green"
                      />
                      <VStack spacing={4} align="start">
                        <Text
                          fontSize="xl"
                          fontWeight="bold"
                          color="gray.800"
                          textTransform="uppercase">
                          UNIFIED MARKETING ROI DASHBOARD
                        </Text>
                        <Text color="gray.700" lineHeight="1.6">
                          Cut through the noise with a single, actionable view
                          of your marketing performance, across both digital and
                          offline channels.
                        </Text>
                      </VStack>
                    </VStack>
                  </Box>
                  <Box
                    width="100%"
                    height={{ base: '200px', md: '336px' }}
                    borderRadius="xl"
                    overflow="hidden">
                    <Image
                      sx={imageZoomSx}
                      src={SOLUTION_IMAGES.SolutionImage1}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      borderRadius="xl"
                    />
                  </Box>
                </Stack>

                {/* Row 2: Placeholder + AI-Powered Modeling */}
                <Stack
                  gap={6}
                  width="100%"
                  direction={{ base: 'column', md: 'row' }}>
                  <Box
                    width="100%"
                    height={{ base: '200px', md: '336px' }}
                    borderRadius="xl"
                    overflow="hidden">
                    <Image
                      sx={imageZoomSx}
                      src={SOLUTION_IMAGES.SolutionImage2}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      borderRadius="xl"
                    />
                  </Box>
                  <Box
                    maxWidth={{ base: '100%', md: '543px' }}
                    width="100%"
                    height="336px">
                    <VStack
                      spacing={6}
                      p={6}
                      bg="background.lightGrey"
                      borderRadius="xl"
                      height="336px"
                      justifyContent={'space-between'}
                      align="start">
                      <Icon
                        iconName="could-ai"
                        width={14}
                        height={14}
                        color="green"
                      />
                      <VStack spacing={4} align="start">
                        <Text
                          fontSize="xl"
                          fontWeight="bold"
                          color="gray.800"
                          textTransform="uppercase">
                          AI-POWERED MODELING IN HOURS, NOT WEEKS
                        </Text>
                        <Text color="gray.700" lineHeight="1.6">
                          Our GenAI-backed engine builds localized,
                          Bayesian-based models automatically, surfacing what
                          drives both short- and long-term sales.
                        </Text>
                      </VStack>
                    </VStack>
                  </Box>
                </Stack>

                {/* Row 3: Scenario Planning + Placeholder */}
                <Stack
                  gap={6}
                  width="100%"
                  direction={{ base: 'column', md: 'row' }}>
                  <Box
                    maxWidth={{ base: '100%', md: '543px' }}
                    width="100%"
                    height="336px">
                    <VStack
                      spacing={6}
                      p={6}
                      bg="background.lightGrey"
                      borderRadius="xl"
                      height="336px"
                      justifyContent={'space-between'}
                      align="start">
                      <Icon
                        iconName="scenario"
                        width={14}
                        height={14}
                        color="green"
                      />
                      <VStack spacing={4} align="start">
                        <Text
                          fontSize="xl"
                          fontWeight="bold"
                          color="gray.800"
                          textTransform="uppercase">
                          SCENARIO PLANNING & BUDGET SIMULATION
                        </Text>
                        <Text color="gray.700" lineHeight="1.6">
                          Test, adjust, and plan with confidence. Shift spend
                          across channels and see the projected ROI in real
                          time.
                        </Text>
                      </VStack>
                    </VStack>
                  </Box>
                  <Box
                    width="100%"
                    height={{ base: '200px', md: '336px' }}
                    borderRadius="xl"
                    overflow="hidden">
                    <Image
                      sx={imageZoomSx}
                      src={SOLUTION_IMAGES.SolutionImage3}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      borderRadius="xl"
                    />
                  </Box>
                </Stack>

                {/* Row 4: Placeholder + Flighting & Pacing Tools */}
                <Stack
                  gap={6}
                  width="100%"
                  direction={{ base: 'column', md: 'row' }}>
                  <Box
                    width="100%"
                    height={{ base: '200px', md: '336px' }}
                    borderRadius="xl"
                    overflow="hidden">
                    <Image
                      sx={imageZoomSx}
                      src={SOLUTION_IMAGES.SolutionImage4}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      borderRadius="xl"
                    />
                  </Box>
                  <Box
                    maxWidth={{ base: '100%', md: '543px' }}
                    width="100%"
                    height="336px">
                    <VStack
                      spacing={6}
                      p={6}
                      bg="background.lightGrey"
                      borderRadius="xl"
                      height="336px"
                      justifyContent={'space-between'}
                      align="start">
                      <Icon
                        iconName="calendar"
                        width={14}
                        height={14}
                        color="green"
                      />
                      <VStack spacing={4} align="start">
                        <Text
                          fontSize="xl"
                          fontWeight="bold"
                          color="gray.800"
                          textTransform="uppercase">
                          FLIGHTING & PACING TOOLS FOR REAL CAMPAIGNS
                        </Text>
                        <Text color="gray.700" lineHeight="1.6">
                          Plan burst or always-on media with tools designed for
                          campaign rhythm and seasonal nuance, not just
                          big-picture theory.
                        </Text>
                      </VStack>
                    </VStack>
                  </Box>
                </Stack>

                {/* Row 5: Instant Reports + Placeholder */}
                <Stack
                  gap={6}
                  width="100%"
                  direction={{ base: 'column', md: 'row' }}>
                  <Box
                    maxWidth={{ base: '100%', md: '543px' }}
                    width="100%"
                    height="336px">
                    <VStack
                      spacing={6}
                      p={6}
                      bg="background.lightGrey"
                      borderRadius="xl"
                      height="336px"
                      justifyContent={'space-between'}
                      align="start">
                      <Icon
                        iconName="report"
                        width={14}
                        height={14}
                        color="green"
                      />
                      <VStack spacing={4} align="start">
                        <Text
                          fontSize="xl"
                          fontWeight="bold"
                          color="gray.800"
                          textTransform="uppercase">
                          INSTANT REPORTS. STAKEHOLDER-READY.
                        </Text>
                        <Text color="gray.700" lineHeight="1.6">
                          Auto-generate presentation-ready PDFs to share ROI
                          insights with leadership, media teams, or clients.
                        </Text>
                      </VStack>
                    </VStack>
                  </Box>
                  <Box
                    width="100%"
                    height={{ base: '200px', md: '336px' }}
                    borderRadius="xl"
                    overflow="hidden">
                    <Image
                      sx={imageZoomSx}
                      src={SOLUTION_IMAGES.SolutionImage5}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      borderRadius="xl"
                    />
                  </Box>
                </Stack>
              </VStack>
            </VStack>
          </Container>
        </Box>
      </RevealOnScroll>
    </Box>
  );
};
